import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import {
  FolderOpen, Download, CheckCircle, AlertCircle, Loader2, FileVideo,
  Settings, RotateCcw, Info, ChevronLeft, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { UpgradeModal } from './subscription';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';

interface CompressionSettings {
  quality: 'high' | 'medium' | 'low' | 'custom';
  format: 'mp4' | 'webm' | 'mov';
  resolution: 'original' | '1080p' | '720p' | '480p' | 'custom';
  bitrate: number;
  codec: 'h264' | 'h265' | 'vp9';
  frameRate: number;
  twoPass: boolean;
}

interface ProcessedVideo {
  id: string;
  originalFileName: string;
  originalSize: number;
  compressedSize: number;
  settings: CompressionSettings;
  downloadUrl: string;
  compressionRatio: number;
  completedAt: Date;
}

interface VideoFile {
  id: string;
  file: File;
  status: 'ready' | 'uploading' | 'uploaded' | 'processing' | 'processed' | 'error';
  progress: number;
  statusText?: string;
  width?: number;
  height?: number;
  duration?: number;
  sourceBitrate?: number;
  s3Key?: string; // set when picked from My Library (already staged in S3 — skip upload)
}

const qualityPresets = {
  high: { bitrate: 8000, description: 'Best quality, larger file size' },
  medium: { bitrate: 4000, description: 'Balanced quality and size' },
  low: { bitrate: 1500, description: 'Smaller size, reduced quality' },
  custom: { bitrate: 4000, description: 'Custom settings' }
};

// File size limit for non-subscribers (10MB)
const MAX_FILE_SIZE_FREE = 10 * 1024 * 1024;

const resolutionOptions = {
  'original': 'Keep Original',
  '1080p': '1920x1080 (Full HD)',
  '720p': '1280x720 (HD)',
  '480p': '854x480 (SD)',
  'custom': 'Custom Resolution'
};

const formatOptions = {
  'mp4': { label: 'MP4', description: 'Most universal format' },
  'webm': { label: 'WebM', description: 'Optimized for web' },
  'mov': { label: 'MOV', description: 'Apple ecosystem' }
};

// Reads real resolution + duration from the file in-browser (no upload needed)
// so size estimates are based on the actual video, not a guessed bitrate.
const probeVideoMetadata = (
  file: File
): Promise<{ width: number; height: number; duration: number }> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out reading video metadata'));
    }, 15000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      const meta = {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      };
      cleanup();
      resolve(meta);
    };
    video.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      reject(new Error('Could not read video metadata'));
    };
    video.src = url;
  });

// Estimated output size = target bitrate x duration. Re-encoding at a lower
// bitrate can't make a file larger, so the source size is the ceiling.
const estimateCompressedSize = (
  file: VideoFile,
  targetBitrateKbps: number
): number | undefined => {
  if (!file.duration || file.duration <= 0) return undefined;
  if (file.file.size === 0) return undefined; // library pick — no local source size to estimate against
  const estBytes = ((targetBitrateKbps * 1000) / 8) * file.duration;
  return Math.round(Math.min(estBytes, file.file.size));
};

// Suggests the highest-quality preset that still meaningfully shrinks the
// source. Returns null when the source is already at/below every preset.
const recommendQuality = (
  sourceBitrateKbps: number | undefined
): 'low' | 'medium' | 'high' | null => {
  if (!sourceBitrateKbps) return null;
  const keys: ('low' | 'medium' | 'high')[] = ['low', 'medium', 'high'];
  const usable = keys.filter(k => qualityPresets[k].bitrate < sourceBitrateKbps * 0.9);
  if (usable.length === 0) return null;
  const target = sourceBitrateKbps / 2;
  return usable.reduce((best, k) =>
    Math.abs(qualityPresets[k].bitrate - target) <
    Math.abs(qualityPresets[best].bitrate - target)
      ? k
      : best
  );
};

export const VideoCompressor = () => {
  const [files, setFiles] = useState<VideoFile[]>([]);
  const [processedVideos, setProcessedVideos] = useState<ProcessedVideo[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [settings, setSettings] = useState<CompressionSettings>({
    quality: 'medium',
    format: 'mp4',
    resolution: 'original',
    bitrate: 4000,
    codec: 'h264',
    frameRate: 30,
    twoPass: false
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const { toast } = useToast();
  const { isSubscriber } = useSubscription();
  const { getAuthHeaders } = useAuth();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const videoFiles = acceptedFiles.filter(file => file.type.startsWith('video/'));

    if (videoFiles.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please select video files only",
        variant: "destructive"
      });
      return;
    }

    // Check file size limit for non-subscribers - show upgrade modal
    if (!isSubscriber) {
      const oversizedFiles = videoFiles.filter(file => file.size > MAX_FILE_SIZE_FREE);
      if (oversizedFiles.length > 0) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const newFiles: VideoFile[] = videoFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'ready',
      progress: 0,
      statusText: 'Reading video…',
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Probe each file's real resolution + bitrate so size estimates aren't guesses
    newFiles.forEach((vf) => {
      probeVideoMetadata(vf.file)
        .then(({ width, height, duration }) => {
          const sourceBitrate =
            duration > 0 ? Math.round((vf.file.size * 8) / duration / 1000) : undefined;
          setFiles(prev => prev.map(f =>
            f.id === vf.id
              ? { ...f, width, height, duration, sourceBitrate, statusText: 'Ready to compress' }
              : f
          ));
        })
        .catch(() => {
          setFiles(prev => prev.map(f =>
            f.id === vf.id ? { ...f, statusText: 'Ready to compress' } : f
          ));
        });
    });
  }, [toast, isSubscriber]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
    }
  });

  // "Choose from your library" — the staged key is already in S3, so
  // startCompression skips the upload for it.
  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const newFile: VideoFile = {
      id: Math.random().toString(36).substr(2, 9),
      file: new File([], stagedFileName(f), { type: 'video/mp4' }),
      status: 'ready',
      progress: 0,
      statusText: 'From your library — ready to compress',
      s3Key: f.key,
    };
    setFiles(prev => [...prev, newFile]);
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setFiles(prev => prev.map(x =>
        x.id === newFile.id
          ? { ...x, width: meta.width, height: meta.height, duration: meta.duration }
          : x
      ));
    } catch { /* size estimates simply stay unavailable */ }
  };

  const updateSettings = (key: keyof CompressionSettings, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      
      // Auto-update bitrate when quality preset changes
      if (key === 'quality' && value !== 'custom') {
        newSettings.bitrate = qualityPresets[value as keyof typeof qualityPresets].bitrate;
      }
      
      // Auto-update codec based on format
      if (key === 'format') {
        if (value === 'webm') {
          newSettings.codec = 'vp9';
        } else {
          newSettings.codec = 'h264';
        }
      }
      
      return newSettings;
    });
  };

  const getCompressionRatio = (originalSize: number, estimatedSize: number) => {
    if (!originalSize) return 0; // library picks have no local source size
    return Math.round(((originalSize - estimatedSize) / originalSize) * 100);
  };

  const startCompression = async () => {
    if (files.length === 0) {
      toast({
        title: "No files",
        description: "Please add videos to compress",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      toast({
        title: "Compression started",
        description: `Compressing ${files.length} video${files.length > 1 ? 's' : ''} with ${settings.quality} quality...`
      });

      for (const fileItem of files) {
        try {
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'processing', statusText: 'Compressing video...' }
              : f
          ));

          // Library picks are already staged in S3 — skip the upload
          let key: string;
          if (fileItem.s3Key) {
            key = fileItem.s3Key;
          } else {
            // Step 1: Get upload URL
            const uploadUrlResponse = await fetch('/get-upload-url', {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({
                fileName: fileItem.file.name,
                fileType: fileItem.file.type || 'video/mp4',
                ephemeral: true
              })
            });

            if (!uploadUrlResponse.ok) {
              throw new Error('Failed to get upload URL');
            }

            const { uploadUrl, key: uploadedKey } = await uploadUrlResponse.json();
            key = uploadedKey;

            // Step 2: Upload to S3
            setFiles(prev => prev.map(f =>
              f.id === fileItem.id
                ? { ...f, status: 'uploading', statusText: 'Uploading...', progress: 0 }
                : f
            ));

            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              body: fileItem.file,
              headers: {
                'Content-Type': fileItem.file.type || 'video/mp4'
              }
            });

            if (!uploadResponse.ok) {
              throw new Error('Failed to upload video');
            }

            setFiles(prev => prev.map(f =>
              f.id === fileItem.id
                ? { ...f, status: 'uploaded', statusText: 'Upload complete', progress: 100 }
                : f
            ));
          }

          // Step 3: Compress video
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'processing', statusText: 'Compressing video...', progress: 0 }
              : f
          ));

          // /api/compress-video runs the encode inline on the web container against a
          // 600s ffmpeg timeout that exactly matches the ALB idle timeout, so anything
          // that takes longer than ten minutes dies with no way to recover. The -auto
          // endpoint has always had the >100MB hand-off to SQS; nothing ever called it,
          // so every compression at every size ran inline and the worker sat idle. A
          // 384MB .mov timed out twice for a charter member on Sep 1.
          const compressResponse = await fetch('/api/compress-video-auto', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              videoKey: key,
              quality: settings.quality,
              format: settings.format,
              resolution: settings.resolution,
              bitrate: settings.bitrate,
              codec: settings.codec,
              frameRate: settings.frameRate,
              twoPass: settings.twoPass
            })
          });

          if (!compressResponse.ok) {
            throw new Error('Failed to start compression');
          }

          let result = await compressResponse.json();

          // Large files come back as a queued job instead of a finished encode. Poll
          // until the worker finishes; there is no request held open, so no cliff.
          if (result.async && result.jobId) {
            const jobId = result.jobId as string;
            const started = Date.now();
            const MAX_WAIT_MS = 60 * 60 * 1000;   // an hour; the worker has no timeout of its own
            for (;;) {
              if (Date.now() - started > MAX_WAIT_MS) {
                throw new Error('Compression is taking longer than expected. We will email you when it is ready.');
              }
              await new Promise(r => setTimeout(r, 5000));
              const st = await fetch(`/api/job-status/${jobId}`, { headers: getAuthHeaders() });
              if (!st.ok) continue;
              const job = await st.json();
              if (typeof job.progress === 'number') {
                setFiles(prev => prev.map(f =>
                  f.id === fileItem.id
                    ? { ...f, statusText: `Compressing video… ${job.progress}%`, progress: job.progress }
                    : f
                ));
              }
              if (job.status === 'completed') { result = job; break; }
              if (job.status === 'failed') throw new Error(job.error || 'Compression failed');
            }
          }

          const downloadUrl = result.downloadUrl;

          // Prefer the real compressed size from the backend; fall back to a
          // duration-based estimate so the number reflects the actual encode.
          const compressedSize =
            typeof result.compressedSize === 'number'
              ? result.compressedSize
              : estimateCompressedSize(fileItem, settings.bitrate) ?? fileItem.file.size;
          const compressionRatio = getCompressionRatio(fileItem.file.size, compressedSize);

          const processedVideo: ProcessedVideo = {
            id: Math.random().toString(36).substr(2, 9),
            originalFileName: fileItem.file.name,
            originalSize: fileItem.file.size,
            compressedSize,
            settings: { ...settings },
            downloadUrl,
            compressionRatio,
            completedAt: new Date()
          };

          setProcessedVideos(prev => [...prev, processedVideo]);
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'processed', statusText: `Compressed! ${compressionRatio}% smaller` }
              : f
          ));

        } catch (error) {
          console.error(`Error compressing ${fileItem.file.name}:`, error);
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'error', statusText: 'Compression failed' }
              : f
          ));
        }
      }

      const successCount = files.filter(f => f.status === 'processed').length;
      toast({
        title: "Compression complete",
        description: `Successfully compressed ${successCount} out of ${files.length} videos!`
      });

    } catch (error) {
      console.error('Compression error:', error);
      toast({
        title: "Compression failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const clearAll = () => {
    setFiles([]);
    setIsProcessing(false);
  };

  const clearProcessedVideos = () => {
    setProcessedVideos([]);
  };

  const removeProcessedVideo = (id: string) => {
    setProcessedVideos(prev => prev.filter(v => v.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatBitrate = (kbps: number) =>
    kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbps` : `${Math.round(kbps)} kbps`;

  const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
  const estimatedCompressedSize = files.reduce(
    (sum, file) => sum + (estimateCompressedSize(file, settings.bitrate) ?? file.file.size),
    0
  );
  const sourceBitrates = files
    .map(f => f.sourceBitrate)
    .filter((b): b is number => typeof b === 'number' && b > 0);
  const minSourceBitrate = sourceBitrates.length ? Math.min(...sourceBitrates) : undefined;
  const recommended = recommendQuality(minSourceBitrate);
  const targetExceedsSource =
    minSourceBitrate !== undefined && settings.bitrate >= minSourceBitrate;
  const uploadedCount = files.filter(f => f.status === 'uploaded').length;
  const processedCount = processedVideos.length;

  const qualityCards: { k: 'low' | 'medium' | 'high'; label: string; sub: string }[] = [
    { k: 'low',    label: 'Smallest',  sub: '1.5 Mbps · max savings' },
    { k: 'medium', label: 'Balanced',  sub: '4 Mbps · default pick' },
    { k: 'high',   label: 'Best',      sub: '8 Mbps · least loss' },
  ];

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '20px 0 10px', fontSize: 13 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Compressor</span>
      </div>

      {/* Header */}
      <header
        style={{
          padding: '10px 0 28px',
          borderBottom: '1px solid var(--hj-hairline-soft)',
          marginBottom: 28,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10 }}>Compressor</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            lineHeight: 1,
            color: 'var(--hj-ink)',
            letterSpacing: '-0.01em',
          }}
        >
          Shrink the file.
        </h1>
        <p
          className="m-0"
          style={{
            marginTop: 12,
            color: 'var(--hj-ink)',
            fontSize: 14.5,
            maxWidth: 560,
            lineHeight: 1.5,
          }}
        >
          Drop a clip, pick a quality target, hit compress. Keep the cinema.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
        {/* Main */}
        <section
          className="flex flex-col gap-4"
        >
          {/* Drop zone */}
          <div
            style={{
              padding: 22,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div
              {...getRootProps()}
              className="text-center cursor-pointer transition-colors"
              style={{
                padding: '36px 24px',
                borderRadius: 'var(--hj-r-md)',
                border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                marginBottom: files.length > 0 ? 16 : 0,
              }}
            >
              <input {...getInputProps()} />
              <div
                className="mx-auto flex items-center justify-center"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-3)',
                  color: 'var(--hj-ink-2)',
                }}
              >
                <FolderOpen size={22} strokeWidth={1.6} />
              </div>
              <p
                className="m-0"
                style={{
                  marginTop: 12,
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--hj-ink)',
                }}
              >
                Drop videos here or click to browse
              </p>
              <p className="mono-label" style={{ marginTop: 6, fontSize: 9 }}>
                MP4 · MOV · AVI · MKV · WebM
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowLibraryPicker(true)}
              className="inline-flex items-center gap-2"
              style={{
                marginTop: 10,
                marginBottom: files.length > 0 ? 16 : 0,
                padding: '8px 12px',
                borderRadius: 'var(--hj-r-sm)',
                border: '1px solid var(--hj-hairline)',
                background: 'transparent',
                color: 'var(--hj-ink-2)',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <FolderOpen size={14} strokeWidth={1.8} />
              Choose from your library
            </button>

            {/* File List */}
            {files.length > 0 && (
              <div className="flex flex-col" style={{ gap: 8 }}>
                {files.map((fileItem) => {
                  const estimatedSize = estimateCompressedSize(fileItem, settings.bitrate);
                  const isProcessingRow = fileItem.status === 'processing';
                  const isProcessed = fileItem.status === 'processed';
                  const isError = fileItem.status === 'error';
                  const accent = isProcessed
                    ? 'var(--hj-green)'
                    : isProcessingRow
                      ? 'var(--hj-blue)'
                      : isError
                        ? 'hsl(var(--destructive))'
                        : 'var(--hj-hairline-soft)';
                  return (
                    <div
                      key={fileItem.id}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 'var(--hj-r-sm)',
                        background: 'var(--hj-surface-2)',
                        border: `1px solid ${accent === 'var(--hj-hairline-soft)' ? 'var(--hj-hairline-soft)' : accent + '88'}`,
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {isProcessingRow && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: 'var(--hj-yellow-ink)' }} />}
                          {isProcessed && <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} />}
                          {isError && <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(var(--destructive))' }} />}
                          <span
                            className="truncate"
                            style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--hj-ink)' }}
                          >
                            {fileItem.file.name}
                          </span>
                        </div>
                        {fileItem.status === 'ready' && (
                          <button
                            type="button"
                            onClick={() => removeFile(fileItem.id)}
                            aria-label="Remove"
                            className="flex items-center justify-center flex-shrink-0"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 999,
                              background: 'transparent',
                              border: 0,
                              color: 'var(--hj-ink-3)',
                              cursor: 'pointer',
                            }}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div
                        className="tabular"
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: 'var(--hj-ink-3)',
                          fontFamily: 'var(--hj-font-mono)',
                          display: 'flex',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>{fileItem.file.size > 0 ? formatFileSize(fileItem.file.size) : 'Library'}</span>
                        {fileItem.width && fileItem.height && (
                          <span>{fileItem.width}×{fileItem.height}</span>
                        )}
                        {fileItem.sourceBitrate && (
                          <span>{formatBitrate(fileItem.sourceBitrate)}</span>
                        )}
                        {estimatedSize !== undefined && (
                          <span style={{ color: 'var(--hj-yellow-ink)' }}>
                            → ~{formatFileSize(estimatedSize)}
                          </span>
                        )}
                        {fileItem.statusText && <span>· {fileItem.statusText}</span>}
                      </div>
                      {fileItem.progress > 0 && isProcessingRow && (
                        <div
                          className="rounded-full overflow-hidden"
                          style={{
                            marginTop: 8,
                            height: 4,
                            background: 'var(--hj-surface-3)',
                          }}
                        >
                          <div
                            className="h-full transition-all"
                            style={{ width: `${fileItem.progress}%`, background: 'var(--hj-blue)' }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Size Comparison */}
          {files.length > 0 && (
            <div
              style={{
                padding: 22,
                borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
              }}
            >
              <div className="mono-label" style={{ marginBottom: 12 }}>Size comparison</div>
              <div className="grid grid-cols-2 gap-4 items-center">
                <div>
                  <div
                    className="tabular"
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: 'var(--hj-ink)',
                      fontFamily: 'var(--hj-font-mono)',
                      lineHeight: 1,
                    }}
                  >
                    {formatFileSize(totalSize)}
                  </div>
                  <div className="mono-label" style={{ marginTop: 6 }}>Original size</div>
                </div>
                <div>
                  <div
                    className="tabular"
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: 'var(--hj-yellow-ink)',
                      fontFamily: 'var(--hj-font-mono)',
                      lineHeight: 1,
                    }}
                  >
                    {formatFileSize(estimatedCompressedSize)}
                  </div>
                  <div className="mono-label" style={{ marginTop: 6 }}>Estimated size</div>
                </div>
              </div>
              <div
                className="inline-flex items-center mono-label"
                style={{
                  marginTop: 14,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--hj-blue-tint)',
                  color: 'var(--hj-yellow-ink)',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {getCompressionRatio(totalSize, estimatedCompressedSize)}% reduction
              </div>
              {targetExceedsSource && (
                <div
                  className="flex items-start gap-1.5"
                  style={{
                    marginTop: 12,
                    fontSize: 11.5,
                    color: 'hsl(var(--destructive))',
                    lineHeight: 1.45,
                  }}
                >
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Your target ({formatBitrate(settings.bitrate)}) is at or above the
                    source bitrate
                    {minSourceBitrate !== undefined
                      ? ` (~${formatBitrate(minSourceBitrate)})`
                      : ''}
                    . The file won't get smaller — pick a more aggressive target.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Processed Videos */}
          {processedVideos.length > 0 && (
            <div
              style={{
                padding: 22,
                borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
              }}
            >
              <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <div className="mono-label">Compressed videos</div>
                <button
                  type="button"
                  onClick={clearProcessedVideos}
                  className="mono-label"
                  style={{
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-hairline)',
                    color: 'var(--hj-ink-3)',
                    fontSize: 9,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-col" style={{ gap: 8 }}>
                {processedVideos.map((video) => (
                  <div
                    key={video.id}
                    className="flex items-center gap-3"
                    style={{
                      padding: '12px 14px',
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'oklch(0.42 0.15 145 / 0.10)',
                      border: '1px solid var(--hj-green)',
                    }}
                  >
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="truncate"
                          style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--hj-ink)' }}
                        >
                          {video.originalFileName.replace(/\.[^/.]+$/, '')}_compressed.{video.settings.format}
                        </span>
                        <span
                          className="mono-label"
                          style={{
                            padding: '2px 7px',
                            borderRadius: 999,
                            background: 'var(--hj-blue-tint)',
                            color: 'var(--hj-yellow-ink)',
                            fontSize: 9,
                            fontWeight: 700,
                          }}
                        >
                          −{video.compressionRatio}%
                        </span>
                      </div>
                      <div
                        className="tabular"
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: 'var(--hj-ink-3)',
                          fontFamily: 'var(--hj-font-mono)',
                        }}
                      >
                        {formatFileSize(video.originalSize)} → {formatFileSize(video.compressedSize)} · {video.settings.quality} · {video.settings.format.toUpperCase()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (video.downloadUrl && !video.downloadUrl.startsWith('#demo')) {
                          window.open(video.downloadUrl, '_blank');
                        } else {
                          toast({ title: 'Download not available' });
                        }
                      }}
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Download
                    </Button>
                    <button
                      type="button"
                      onClick={() => removeProcessedVideo(video.id)}
                      aria-label="Remove"
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: 'transparent',
                        border: 0,
                        color: 'var(--hj-ink-3)',
                        cursor: 'pointer',
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Settings sidebar */}
        <aside
          className="flex flex-col gap-4"
          style={{ alignSelf: 'start' }}
        >
          {/* Quality target — 3-card group */}
          <div
            style={{
              padding: 18,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div className="mono-label" style={{ marginBottom: 10 }}>Target</div>
            {minSourceBitrate !== undefined && (
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 12,
                  color: 'var(--hj-ink-3)',
                  lineHeight: 1.4,
                }}
              >
                Source ~{formatBitrate(minSourceBitrate)}
                {recommended
                  ? ` · ${qualityCards.find(q => q.k === recommended)?.label} recommended`
                  : ' · already well compressed'}
              </div>
            )}
            <div className="flex flex-col gap-2">
              {qualityCards.map((q) => {
                const active = settings.quality === q.k;
                const presetBitrate = qualityPresets[q.k].bitrate;
                const isRecommended = recommended === q.k;
                const wontShrink =
                  minSourceBitrate !== undefined && presetBitrate >= minSourceBitrate;
                return (
                  <button
                    key={q.k}
                    type="button"
                    onClick={() => updateSettings('quality', q.k)}
                    className="text-left transition-colors"
                    style={{
                      padding: 12,
                      borderRadius: 'var(--hj-r-sm)',
                      background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                      border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                      color: 'var(--hj-ink)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      opacity: wontShrink ? 0.55 : 1,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{q.label}</div>
                      {isRecommended && (
                        <span
                          className="mono-label"
                          style={{
                            padding: '2px 7px',
                            borderRadius: 999,
                            background: 'var(--hj-blue)',
                            color: 'oklch(0.18 0.005 70)',
                            fontSize: 8,
                            fontWeight: 700,
                          }}
                        >
                          Recommended
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                      {wontShrink ? 'Above source bitrate · no savings' : q.sub}
                    </div>
                  </button>
                );
              })}
              {/* Custom (preserved) */}
              {settings.quality === 'custom' && (
                <div style={{ marginTop: 4 }}>
                  <Label className="mono-label">Bitrate · {settings.bitrate} kbps</Label>
                  <Slider
                    value={[settings.bitrate]}
                    onValueChange={([value]) => updateSettings('bitrate', value)}
                    min={500}
                    max={15000}
                    step={500}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Format + resolution + advanced */}
          <div
            style={{
              padding: 18,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div className="mono-label" style={{ marginBottom: 10 }}>
              <Settings size={11} strokeWidth={2} style={{ display: 'inline', marginRight: 6 }} />
              Settings
            </div>
            <div className="flex flex-col gap-3.5">
              <label className="flex flex-col gap-1.5">
                <span className="mono-label" style={{ fontSize: 9 }}>Output format</span>
                <Select value={settings.format} onValueChange={(value) => updateSettings('format', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(formatOptions).map(([key, format]) => (
                      <SelectItem key={key} value={key}>{format.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="mono-label" style={{ fontSize: 9 }}>Resolution</span>
                <Select value={settings.resolution} onValueChange={(value) => updateSettings('resolution', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(resolutionOptions).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <div
                style={{
                  marginTop: 4,
                  paddingTop: 12,
                  borderTop: '1px solid var(--hj-hairline-soft)',
                }}
              >
                <div className="flex items-center justify-between">
                  <Label className="mono-label" style={{ fontSize: 9 }}>Two-pass encoding</Label>
                  <Switch
                    checked={settings.twoPass}
                    onCheckedChange={(checked) => updateSettings('twoPass', checked)}
                  />
                </div>
                {settings.twoPass && (
                  <div className="flex items-start gap-1.5" style={{ marginTop: 6, fontSize: 11, color: 'var(--hj-ink-2)' }}>
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Better quality, takes twice as long.</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              onClick={startCompression}
              disabled={files.length === 0 || isProcessing}
              size="lg"
              className="w-full"
            >
              {isProcessing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compressing…</>
              ) : (
                <><FileVideo className="w-4 h-4 mr-2" /> Compress {files.length} video{files.length !== 1 ? 's' : ''}</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={clearAll}
              disabled={files.length === 0 || isProcessing}
              className="w-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear all
            </Button>
          </div>
        </aside>
      </div>

      {/* Success summary */}
      {processedCount > 0 && !isProcessing && (
        <div
          className="flex items-center gap-2.5"
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 'var(--hj-r-sm)',
            background: 'oklch(0.42 0.15 145 / 0.12)',
            border: '1px solid var(--hj-green)',
            color: 'var(--hj-ink)',
            fontSize: 13,
          }}
        >
          <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
          Compressed {processedCount} video{processedCount > 1 ? 's' : ''} · downloads ready above.
        </div>
      )}

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger="video-tools"
      />

      <LibraryPicker
        open={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />
    </main>
  );
};
